import { Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { Toaster } from '@/components/ui/sonner';

import { Button } from '@/components/ui/button';

import {
  can,
  errorMessage,
  getMe,
  listProjects,
  listTickets,
  logout,
  NotLoggedInError,
  type Project,
  type TicketCard,
  type User,
} from './api';
import { BoardPage } from './BoardPage';
import { columnLabel } from './columns';
import { LoginPage } from './LoginPage';
import { Logo } from './Logo';
import { AccessToggle } from './AccessToggle';
import { NotificationBell } from './NotificationBell';
import { TrashPage } from './TrashPage';
import { SettingsPage } from './SettingsPage';
import { TicketDialog } from './ticket/TicketDialog';

type Auth =
  | { status: 'loading' }
  | { status: 'boot-error'; message: string }
  | { status: 'guest' }
  | { status: 'in'; user: User };

function crumbBoard(path: string): boolean {
  return path === '/' || path.startsWith('/tickets');
}

function crumbSettings(path: string): boolean {
  return path.startsWith('/settings');
}

const PROJECT_STORAGE_KEY = 'plusme-office.project';

function readStoredProjectId(): number | null {
  try {
    const raw = window.localStorage.getItem(PROJECT_STORAGE_KEY);
    if (raw === null || raw === '') return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function writeStoredProjectId(id: number): void {
  try {
    window.localStorage.setItem(PROJECT_STORAGE_KEY, String(id));
  } catch {
    // 저장을 못 해도 고른 값은 이번 세션에서 쓴다.
  }
}

function pickProjectId(projects: Project[], stored: number | null): number | null {
  if (stored !== null && projects.some((project) => project.id === stored)) {
    return stored;
  }
  return projects[0]?.id ?? null;
}


export function App() {
  const [auth, setAuth] = useState<Auth>({ status: 'loading' });
  const [navOpenFor, setNavOpenFor] = useState<string | null>(null);
  /** 검색 결과로 연 티켓. **화면을 안 옮긴다**(2026-08-29 지시) —
      검색은 GNB 라 어느 화면에서나 부르고, 그때마다 자리를 옮기면
      하던 일이 끊긴다. */
  const [searchTicketId, setSearchTicketId] = useState<number | null>(null);
  /** 좁은 화면(720px 이하)에서 검색줄을 펼쳤나(2026-10-19 모바일 개선).
      머리줄이 두 줄(113px)을 먹어 본문이 밀렸다 — 평소엔 돋보기 단추만 둔다. */
  const [searchOpen, setSearchOpen] = useState(false);
  /**
   * 넓은 화면에서 사이드바를 접었나(2026-08-26). **좁은 화면과 다른 축이다** —
   * 거기서는 서랍(`navOpenFor`)이고 여기서는 격자에서 빠지는 것이다.
   *
   * 기억한다. 접어 두는 사람은 계속 접어 두고 싶어 한다.
   */
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem('office.nav.collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [projectLoad, setProjectLoad] = useState<'loading' | 'ready' | 'error'>('loading');
  const [projectError, setProjectError] = useState<string | null>(null);
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 960px)').matches,
  );
  const location = useLocation();
  const navOpen = navOpenFor === location.pathname;

  /**
   * 화면들이 오류를 알리는 자리. **토스트로 모았다**(2026-08-26 지시).
   *
   * 옛 판은 본문 위 배너였다 — 보드에서 카드를 옮기다 실패하면 배너가 위에
   * 뜨는데 눈은 카드에 있어서 못 봤다. 오른쪽 위 한 자리로 모으면 어디를
   * 봐야 할지 한 번만 배우면 된다.
   *
   * `null` 은 「지운다」다. 토스트는 스스로 사라지므로 할 일이 없다.
   */
  const onError = useCallback((message: string | null) => {
    if (message) toast.error(message);
  }, []);

  const onUnauthorized = useCallback(() => {
    setAuth({ status: 'guest' });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 960px)');
    const apply = () => setNarrow(mq.matches);
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // 서랍만 남았다. 설정은 페이지가 됐고 팝오버가 없어져 여기서 닫을 것이 없다.
  useEffect(() => {
    if (!navOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setNavOpenFor(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const user = await getMe();
        if (!cancelled) setAuth({ status: 'in', user });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) {
          setAuth({ status: 'guest' });
          return;
        }
        setAuth({
          status: 'boot-error',
          message: `지금 사용자를 확인하지 못했다: ${errorMessage(err)}`,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * 프로젝트 목록을 다시 채운다.
   *
   * **로그인당 한 번만 부르던 것을 콜백으로 뺐다.** 관리 화면에서 프로젝트를
   * 만들거나 저장소를 이었을 때 이것을 부르지 않으면, 헤더 선택기가 새로고침
   * 전까지 옛 목록을 쥔 채로 남는다 — 만든 사람 눈에는 아무 일도 안 일어난
   * 것처럼 보인다.
   *
   * 고른 프로젝트는 **살아 있으면 그대로 둔다.** 여기서 첫째로 되돌리면
   * 저장소를 하나 이을 때마다 보던 프로젝트가 바뀐다.
   */
  const reloadProjects = useCallback(async () => {
    setProjectLoad('loading');
    setProjectError(null);
    try {
      const data = await listProjects();
      const list = data.projects;
      setProjects(list);
      setProjectId((current) => pickProjectId(list, current ?? readStoredProjectId()));
      setProjectLoad('ready');
    } catch (err) {
      setProjectError(errorMessage(err));
      setProjectLoad('error');
      throw err;
    }
  }, []);

  const retryProjects = useCallback(async () => {
    try {
      await reloadProjects();
    } catch (err) {
      if (err instanceof NotLoggedInError) onUnauthorized();
    }
  }, [reloadProjects, onUnauthorized]);

  useEffect(() => {
    if (auth.status !== 'in') return;
    let cancelled = false;
    void (async () => {
      try {
        await reloadProjects();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) {
          onUnauthorized();
          return;
        }
        // 조회 실패는 본문에 남긴다. 선택할 프로젝트가 없는 상태와 구분한다.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.status, reloadProjects, onUnauthorized]);

  // 고른 값을 남기는 자리를 하나로 모았다. 고르는 길이 둘(선택기·목록 갱신)이라
  // 각자 적으면 한쪽을 빠뜨렸을 때 새로고침에 되돌아간다.
  useEffect(() => {
    if (projectId !== null) writeStoredProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    try {
      window.localStorage.setItem('office.nav.collapsed', navCollapsed ? '1' : '0');
    } catch {
      /* 저장을 막아 둔 브라우저에서도 이번 세션은 그대로 돈다. */
    }
  }, [navCollapsed]);

  function handleProjectChange(nextRaw: string) {
    const next = Number(nextRaw);
    if (!Number.isInteger(next) || next <= 0) return;
    if (!projects.some((project) => project.id === next)) return;
    setProjectId(next);
  }

  async function handleLogout() {
    try {
      await logout();
      setAuth({ status: 'guest' });
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        setAuth({ status: 'guest' });
        return;
      }
      toast.error(`로그아웃에 실패했다: ${errorMessage(err)}`);
    }
  }

  if (auth.status === 'loading') {
    return <p className="page-status">불러오는 중…</p>;
  }

  if (auth.status === 'boot-error') {
    return (
      <main className="grid min-h-full place-items-center bg-canvas p-s4">
        <div className="w-[380px] max-w-full">
          <div className="rounded-lg border border-line bg-panel px-s6 pt-s7 pb-s6 shadow-panel">
            <p className="mt-0 mb-s4 text-meta text-ink-2">{auth.message}</p>
            <Button type="button" block onClick={() => window.location.reload()}>
              다시 시도
            </Button>
          </div>
        </div>
      </main>
    );
  }

  if (auth.status === 'guest') {
    return (
      <LoginPage
        onLoggedIn={(user) => {
                setAuth({ status: 'in', user });
        }}
      />
    );
  }

  const { user } = auth;
  const path = location.pathname;
  const boardCurrent = crumbBoard(path);
  const settingsCurrent = crumbSettings(path);
  // 서랍이 닫혔거나(좁은 화면) 접혔으면(넓은 화면) 읽는 쪽에서도 빠진다.
  const navHidden = narrow ? !navOpen : navCollapsed;

  // ⚠ **권한 개념이 없다** (2026-08-26). 옛 판은 메뉴를 `page.*` 권한으로
  // 갈랐고 게이트는 서버에 있었다. 칸반 재구축에서 그 층을 통째로 걷었으므로
  // 지금은 로그인한 사람이면 전부 같은 것을 본다. 다시 얹을 때는 **판정을
  // 서버에 두고 여기서는 숨기기만** 해라 — 숨기는 것은 방어가 아니다.

  return (
    /*
     * 뼈대 — **GNB 가 맨 위에서 가로 전체를 차지하고, 그 아래에 LNB 와 본문이
     * 나란히 선다.** 2026-08-25 에 뒤집혔다: 옛 판은 LNB 가 화면 높이를 다
     * 먹고 GNB 는 그 오른쪽 토막이었으며 로고도 LNB 안에 있었다.
     *
     * **DOM 순서가 곧 탭 순서다** — 로고 → GNB → (뒷막) → LNB → 본문.
     * 자리는 `.app` 의 격자가 정하므로 여기서는 읽는 순서대로만 적는다.
     */
    <div
      className={[
        'app',
        navOpen ? 'is-nav-open' : '',
        // 좁은 화면에서는 접힘이 뜻을 잃는다 — 거기서는 서랍이 이미 접혀 있다.
        !narrow && navCollapsed ? 'is-nav-collapsed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* 옛 판은 설정이 열릴 때 `is-settings-open` 을 붙여 상단바의 `overflow` 를
          풀었다. 팝오버가 상단바 안에 그려져 잘렸기 때문이다. Popover 는 내용을
          body 로 내보내므로(portal) 그 우회가 필요 없다. */}
      <header className={searchOpen ? 'topbar is-search-open' : 'topbar'}>
        {/* 로고는 GNB 왼쪽 끝이고 **여전히 홈으로 가는 링크**다.
            ⚠ `aria-label` 을 지우지 마라 — 좁은 화면에서는 제품명을 `display: none`
            으로 접는데, 그러면 읽는 쪽에 남는 것이 `aria-hidden` 인 심볼뿐이라
            **이름 없는 링크**가 된다(2026-08-25 실측: 375px 에서 이름이 비었다). */}
        {/* **로고와 햄버거는 한 칸이다.** 상단바가 3열 격자라(좌·검색·우)
            햄버거를 따로 두면 넷째 항목이 되어 줄이 밀린다(실측). */}
        <div className="topbar-left">
          <Link to="/" className="nav-brand" aria-label="PLUSME OFFICE 홈">
            <Logo />
            <span className="wordmark">PLUSME OFFICE</span>
          </Link>
        {/*
          햄버거 하나가 **화면 폭에 따라 다른 일을 한다** — 좁으면 서랍을
          여닫고, 넓으면 사이드바를 접었다 편다. 손잡이를 둘로 나누면
          한쪽은 늘 쓸모없는 자리에 있게 된다.
        */}
        <button
          type="button"
          className="ib nav-toggle"
          aria-label={narrow ? '메뉴' : navCollapsed ? '사이드바 펼치기' : '사이드바 접기'}
          aria-expanded={narrow ? navOpen : !navCollapsed}
          onClick={() => {
            if (narrow) {
              setNavOpenFor((current) =>
                current === location.pathname ? null : location.pathname,
              );
            } else {
              setNavCollapsed((v) => !v);
            }
          }}
        >
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
            <path
              d="M3 4.5h11M3 8.5h11M3 12.5h11"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          </button>
          {/* **넓은 화면의 메뉴는 머리줄 탭이다**(2026-10-18 C 현황판형).
              사이드바를 걷고 같은 세 곳을 여기 세운다. 좁은 화면에서는
              숨기고 서랍(아래 `.nav`)이 그대로 맡는다. */}
          <nav className="top-tabs" aria-label="주 메뉴">
            <Link to="/" aria-current={boardCurrent ? 'page' : undefined}>칸반보드</Link>
            {can(user, 'ticket.delete') && (
              <Link to="/trash" aria-current={path === '/trash' ? 'page' : undefined}>휴지통</Link>
            )}
            <Link to="/settings" aria-current={settingsCurrent ? 'page' : undefined}>설정</Link>
          </nav>
        </div>
        {/*
          **검색은 GNB 한가운데다**(2026-08-26 지시). `.topbar` 가 3열 격자라
          이 칸이 화면 중앙에 선다 — flex 의 `margin: auto` 로 밀면 왼쪽
          로고 폭에 따라 중앙이 흔들린다.
        */}
        <TicketSearch
          projectId={projectId}
          onOpen={setSearchTicketId}
          onError={onError}
          onUnauthorized={onUnauthorized}
        />
        <div className="top-right">
          {/* ⚠ **프로젝트 선택은 여기 없다**(2026-08-26 지시). 업무 칸반의
              도구줄로 옮겼다 — 고르는 자리와 그 결과를 보는 자리가 같아야
              한다. GNB 에는 어느 프로젝트인지도 안 적는다. */}
          {/* 알림 종(2026-09-01). 알림을 누르면 검색과 **같은 모달**
              (`searchTicketId` → 아래 TicketDialog)로 그 티켓이 열린다 —
              여는 길을 또 만들면 한쪽이 반드시 낡는다. */}
          {/* 보안 접속 토글(2026-09-16 사용자 지정). **`wl.viewster.io` 를
              이어받는 자리다** — 켜면 이 브라우저가 나가는 주소가 정해진
              시간 동안 열린다. 알림 종 **오른쪽**에 둔다(2026-09-17 사용자
              지정) — 로그아웃 바로 왼쪽이라 「나가기 전 손대는 것」끼리 모인다. */}
          <button
            type="button"
            className="ib search-toggle"
            aria-label={searchOpen ? '검색 닫기' : '티켓 검색'}
            aria-expanded={searchOpen}
            onClick={() => {
              setSearchOpen((open) => !open);
              // 펼치자마자 바로 칠 수 있게 입력칸에 초점을 준다.
              requestAnimationFrame(() =>
                document.querySelector<HTMLInputElement>('.topbar .search input')?.focus(),
              );
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="5.2" stroke="currentColor" strokeWidth="1.6" />
              <path d="m12 12 3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
          <NotificationBell
            onOpenTicket={setSearchTicketId}
            onError={onError}
            onUnauthorized={onUnauthorized}
          />
          {/* **좁은 화면에서는 둘이 서랍 아래로 내려간다**(2026-10-03, 390px
              실측: 「보안 접속 켜짐 · N시간 남음」·「모든 기기 끄기」·「로그아웃」
              이 첫 줄을 다 먹어 로고·종·햄버거가 겹쳤다). GNB 에는 로고·메뉴·
              종·검색만 남긴다. 한쪽에만 그린다 — 둘 다 그리면 토글이 상태를
              두 번 읽는다. */}
          {!narrow && (
            <>
              <AccessToggle onUnauthorized={onUnauthorized} />
              <Button type="button" variant="outline" size="sm" onClick={() => void handleLogout()}>
                로그아웃
              </Button>
            </>
          )}
        </div>
      </header>

      <button
        type="button"
        className="nav-backdrop"
        aria-label="메뉴 닫기"
        tabIndex={navOpen ? 0 : -1}
        onClick={() => setNavOpenFor(null)}
      />
      <nav
        className="nav"
        aria-hidden={navHidden || undefined}
        inert={navHidden || undefined}
        onClick={(event) => {
          const target = event.target;
          if (target instanceof Element && target.closest('a')) setNavOpenFor(null);
        }}
      >
        {/*
          **LNB 머리는 「누구로 들어와 있나」다**(2026-08-26 지시).
          아바타를 안 그린다 — 이름과 이메일이 이미 있는데 그 옆의 글자
          하나짜리 원은 같은 것을 두 번 말한다.
        */}
        <div className="nav-who">
          <b>{user.display_name}</b>
          <span>{user.email}</span>
        </div>

        <Link to="/" aria-current={boardCurrent ? 'page' : undefined}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="1.8" y="2.8" width="3.6" height="10.4" rx="1" stroke="currentColor" strokeWidth="1.3" />
            <rect x="6.7" y="2.8" width="3.6" height="7" rx="1" stroke="currentColor" strokeWidth="1.3" />
            <rect x="11.6" y="2.8" width="2.6" height="4.6" rx="1" stroke="currentColor" strokeWidth="1.3" />
          </svg>
          칸반보드
        </Link>

        {/* ⚠ **「프로젝트」 항목을 걷었다**(2026-08-26 지시). 프로젝트와
            라벨은 설정 화면의 카드로 들어갔다 — 그것이 따로 서 있으면 **이
            도구의 단위가 프로젝트인 것처럼 보인다.** 단위는 티켓이다. */}
        {can(user, 'ticket.delete') && <Link to="/trash" aria-current={path === '/trash' ? 'page' : undefined}><Trash2 size={16} aria-hidden="true" />휴지통</Link>}
        <hr className="nav-div" role="presentation" />
        <Link to="/settings" aria-current={settingsCurrent ? 'page' : undefined}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.3" />
            <path
              d="M8 1.4v1.7M8 12.9v1.7M14.6 8h-1.7M3.1 8H1.4M12.7 3.3l-1.2 1.2M4.5 11.5l-1.2 1.2M12.7 12.7l-1.2-1.2M4.5 4.5 3.3 3.3"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          설정
        </Link>

        {/* 계정 묶음 — 좁은 화면에서 GNB 에서 내려온 보안 접속·로그아웃.
            **지운 것이 아니라 옮긴 것이다.** 설정 아래 구분선 다음, 서랍 바닥. */}
        {narrow && (
          <div className="nav-account">
            <AccessToggle onUnauthorized={onUnauthorized} />
            <Button type="button" variant="outline" size="sm" onClick={() => void handleLogout()}>
              로그아웃
            </Button>
          </div>
        )}
      </nav>

      <div className="main">
        <div className="page">
          {!settingsCurrent && projectLoad !== 'ready' ? (
            <div className="page-status" role="status">
              {projectLoad === 'loading' ? '프로젝트를 불러오는 중…' : (
                <>
                  <p>프로젝트를 불러오지 못했습니다. {projectError}</p>
                  <Button onClick={() => void retryProjects()}>다시 시도</Button>
                </>
              )}
            </div>
          ) : !settingsCurrent && projectId === null ? (
            <div className="page-status">
              <p>등록된 프로젝트가 없습니다.</p>
              {user.is_admin
                ? <Link to="/settings?tab=workspace">설정에서 프로젝트 만들기</Link>
                : <p>관리자에게 프로젝트 등록을 요청해 주세요.</p>}
            </div>
          ) : <Routes>
            <Route path="/trash" element={<TrashPage key={projectId} user={user} projectId={projectId} />} />
            {/* **홈이 칸반보드다.** 대시보드를 걷어냈고, 이 도구의 첫 화면은
                「지금 일이 어디까지 갔나」다. */}
            <Route
              path="/"
              element={
                <BoardPage
                  user={user}
                  projectId={projectId}
                  projects={projects}
                  onProjectChange={(id) => handleProjectChange(String(id))}
                  onError={onError}
                  onUnauthorized={onUnauthorized}
                />
              }
            />
            {/* **티켓 주소다 — 페이지가 아니다**(2026-08-29 지시: 상세는
                무조건 모달). 보드를 그리면서 그 티켓의 모달을 연다
                (남이 보낸 링크가 막다른 길이 되면 안 된다). 주소를 만드는 자리는 티켓 상세 머리의
                「링크 복사」이고 모양의 주인은 `ticket/link.ts` 다 —
                이 경로를 바꾸면 그쪽도 같이 바꿔라. */}
            <Route
              path="/tickets/:id"
              element={
                <BoardPage
                  user={user}
                  projectId={projectId}
                  projects={projects}
                  onProjectChange={(id) => handleProjectChange(String(id))}
                  onError={onError}
                  onUnauthorized={onUnauthorized}
                />
              }
            />
            <Route
              path="/settings"
              element={
                <SettingsPage
                  user={user}
                  onSaved={(next) => setAuth({ status: 'in', user: next })}
                  onError={onError}
                  onUnauthorized={onUnauthorized}
                  projects={projects}
                  projectId={projectId}
                  reloadProjects={reloadProjects}
                />
              }
            />
            <Route
              path="*"
              element={
                <p className="page-status">
                  없는 주소다. <Link to="/">보드로</Link>
                </p>
              }
            />
          </Routes>}
        </div>
      </div>

      {/* 검색으로 연 티켓. **`/tickets/:id` 페이지는 2026-08-29 에 걷었다**
          (지시: 티켓 상세는 무조건 모달). 그래서 이 모달이 어느 화면에서든
          상세를 여는 마지막 길이다 — 여기를 지우면 검색이 막다른 길이 된다. */}
      <TicketDialog
        ticketId={searchTicketId}
        user={user}
        projects={projects}
        onError={onError}
        onUnauthorized={onUnauthorized}
        onClose={() => setSearchTicketId(null)}
        // 보드가 자기 목록을 들고 있어 여기서 할 수 있는 것이 없다.
        // 보드에서 연 것은 보드의 모달이 따로 받는다.
        onChanged={() => undefined}
        onDeleted={() => setSearchTicketId(null)}
      />

      {/* **알림은 전부 여기로 온다** — 성공도 실패도 상단 가운데 한 자리다. */}
      <Toaster />
    </div>
  );
}

const SEARCH_DEBOUNCE_MS = 280;
const SEARCH_MIN_CHARS = 2;
// 목록에 몇 장까지 띄우나. 서버는 거른 것을 전부 주고 자르는 것은 여기다 —
// 검색 전용 경로를 따로 두지 않으려고 보드 목록을 그대로 쓴다.
const SEARCH_MAX_RESULTS = 8;

function TicketSearch({
  projectId,
  onOpen,
  onError,
  onUnauthorized,
}: {
  projectId: number | null;
  /** 고른 티켓을 **모달로** 연다(2026-08-29 지시). 페이지로 보내지 않는다. */
  onOpen: (id: number) => void;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<TicketCard[]>([]);
  const [resultsFor, setResultsFor] = useState('');
  const [failed, setFailed] = useState(false);
  const query = draft.trim();
  const canSearch = projectId !== null && query.length >= SEARCH_MIN_CHARS;
  const showMenu = open && canSearch;
  // 프로젝트도 함께 기억한다. 질의만 보면 프로젝트를 바꿨을 때
  // `pending` 이 false 로 남아 **옛 프로젝트의 결과가 그대로 보인다**.
  const resultsKey = `${projectId ?? ''}:${query}`;
  const pending = resultsFor !== resultsKey;

  useEffect(() => {
    if (!showMenu) return;
    function onPointer(event: MouseEvent) {
      const root = wrapRef.current;
      if (root && !root.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [showMenu]);

  useEffect(() => {
    if (!canSearch || projectId === null) return;
    const requested = query;
    const pid = projectId;
    const key = `${pid}:${requested}`;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const data = await listTickets(pid, { q: requested });
          if (cancelled) return;
          setResults(data.tickets.slice(0, SEARCH_MAX_RESULTS));
          setFailed(false);
          setResultsFor(key);
        } catch (err) {
          if (cancelled) return;
          setResults([]);
          // 실패를 「찾은 것 없음」로 보이면 화면이 거짓말을 한다 —
          // 0건과 못 물어본 것은 다르다.
          setFailed(true);
          setResultsFor(key);
          if (err instanceof NotLoggedInError) {
            onUnauthorized();
            return;
          }
          onError(`검색에 실패했다: ${errorMessage(err)}`);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canSearch, query, projectId, onError, onUnauthorized]);

  return (
    <div className="search-wrap" ref={wrapRef}>
      <div className="search">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="6.2" cy="6.2" r="4.4" stroke="currentColor" strokeWidth="1.3" />
          <path d="m9.6 9.6 2.6 2.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="티켓 번호나 제목으로 찾기"
          aria-label="티켓 번호나 제목으로 찾기"
          autoComplete="off"
        />
      </div>
      {showMenu && (
        // `role="listbox"`·`role="option"` 과 `aria-expanded` 를 안 쓴다.
        // 그것들은 combobox 패턴의 일부이고, 화살표 이동과
        // `aria-activedescendant` 없이 붙이면 읽는 쪽이 더 헷갈린다.
        // 링크 목록 그대로가 정확하다 — Tab 으로 이미 이동된다.
        <ul
          className="search-results"
          id="ticket-search-results"
          aria-label="검색 결과"
        >
          {pending ? (
            <li className="empty">찾는 중…</li>
          ) : failed ? (
            <li className="empty">찾지 못했다</li>
          ) : results.length === 0 ? (
            <li className="empty">찾은 것 없음</li>
          ) : (
            results.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onOpen(item.id);
                  }}
                >
                  <span className="id num">#{item.id}</span>
                  <span className="t">{item.title}</span>
                  <span className="col-name">{columnLabel(item.col)}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
